import { ConfigCatProvider } from 'configcat-react';
import { FC, ReactNode } from 'react';

import OneTrust from '@components/OneTrust/index.tsx';
import { OptimizelyWebExperimentation } from '@components/OptimizelyWebExperimentation/index.tsx';
import { SnackbarProvider } from '@components/Snackbar';
import { CONFIGCAT_SDK_KEY } from '@constants/config';
import { OptimizelyProvider } from '@features/Optimisely/Provider';
import { FeatureFlagOverrideProvider } from '@providers/FeatureFlagOverrideProvider';
import { AuthProvider } from '@services/Auth/context.tsx';
import { TraceProvider } from '@services/otel';
import { TrackingContextProvider } from '@tracking/Provider';
import { flagOverrides } from '@utils/flagOverrides.ts';

import { AppointmentProvider } from './AppointmentProvider';
import { BasketProvider, DetailsProvider } from './basket';
import { ApolloProvider } from './external/ApolloProvider';
import { HostedAttributionProvider } from './state/HostedAttributionProvider';
import { FadeProvider } from './ui/FadeProvider';
import { LoaderProvider } from './ui/LoaderProvider';

interface AppProviderProps {
  children: ReactNode;
}

const AppProvider: FC<AppProviderProps> = ({ children }) => (
  <FeatureFlagOverrideProvider>
    <ConfigCatProvider
      sdkKey={CONFIGCAT_SDK_KEY}
      options={{
        flagOverrides: import.meta.env.DEV ? flagOverrides : undefined,
      }}
    >
      <TrackingContextProvider>
        <LoaderProvider>
          <OptimizelyProvider>
            <OptimizelyWebExperimentation />
            <OneTrust />
            <SnackbarProvider>
              <TraceProvider>
                <AuthProvider>
                  <ApolloProvider>
                    <HostedAttributionProvider>
                      <BasketProvider>
                        <DetailsProvider>
                          <AppointmentProvider>
                            <FadeProvider>{children}</FadeProvider>
                          </AppointmentProvider>
                        </DetailsProvider>
                      </BasketProvider>
                    </HostedAttributionProvider>
                  </ApolloProvider>
                </AuthProvider>
              </TraceProvider>
            </SnackbarProvider>
          </OptimizelyProvider>
        </LoaderProvider>
      </TrackingContextProvider>
    </ConfigCatProvider>
  </FeatureFlagOverrideProvider>
);

export { AppProvider };
